// Strategies — read-only catalog of every strategy the runtime
// supports. Each descriptor includes the params spec so an operator
// can see what knobs are tunable before authoring a preset.
import { createFileRoute } from "@tanstack/react-router";
import { ROUTE_CRITICAL_KEYS, refreshOnNavigate } from "@/lib/routeRefresh";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { useQuery } from "@tanstack/react-query";
import { keys, fetchStrategies } from "@/lib/queries";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { formatParamDefault, formatParamValue, paramUnitLabel } from "@/lib/strategyParams";
import { useI18n } from "@/i18n/I18nProvider";
import {
  strategyDescription,
  strategyNameLabel,
  strategyParamKind,
  strategyParamLabel,
  strategyTitle,
} from "@/i18n/strategyCopy";

export const Route = createFileRoute("/_app/strategies")({
  // SM-563 — re-read this page's data on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.strategies),
  component: StrategiesPage,
});

function StrategiesPage() {
  const { t } = useI18n();
  const q = useQuery({ queryKey: keys.strategies(), queryFn: fetchStrategies });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="page-title">{t("strategies.title")}</h1>
        <p className="page-subtitle">
          {t("strategies.subtitle")}
        </p>
      </header>
      <DataFreshness query={q} />

      {q.isLoading && <Skeleton lines={6} />}
      {q.data && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {q.data.strategies.map((s) => (
            <Card key={s.name} className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-t-1">{strategyTitle(s, t)}</h3>
                  <p className="mono mt-0.5 text-xs t-mute">{s.name}</p>
                </div>
                {s.composite && <Badge tone="accent">{t("strategies.composite")}</Badge>}
              </div>
              <p className="text-sm text-t-2">{strategyDescription(s, t)}</p>
              {s.composite && s.composes && (
                <div className="text-xs text-t-3">
                  {t("strategies.composes")}{" "}
                  {s.composes.map((c) => (
                    <code key={c} title={c} className="mono ml-1 rounded bg-bg-3 px-1.5 py-0.5">
                      {strategyNameLabel(c, t)}
                    </code>
                  ))}
                </div>
              )}
              <div className="rounded-md border border-line-1 bg-bg-3 p-3">
                <div className="text-2xs uppercase tracking-widest text-t-3">{t("strategies.parameters")}</div>
                <ul className="mt-2 space-y-1.5 text-xs">
                  {s.params.map((p) => (
                    <li key={p.name} className="flex items-baseline justify-between gap-2">
                      <span className="text-t-1" title={p.name}>{strategyParamLabel(p, t)}</span>
                      <span className="t-mute">
                        {paramUnitLabel(p, strategyParamKind(p, t))}
                        {p.min !== undefined && p.max !== undefined && (
                          <span className="ml-1">[{formatParamValue(p, p.min)}–{formatParamValue(p, p.max)}]</span>
                        )}
                        {p.default !== undefined && (
                          <span className="ml-1">
                            · {t("strategies.default", {
                              value: typeof p.default === "boolean"
                                ? t(p.default ? "common.yes" : "common.no")
                                : formatParamDefault(p) ?? "",
                            })}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
